/**
 * Le password che non si possono scegliere, e il modo di riconoscerle.
 *
 * È la terza parte di A16, quella che mancava: lunghezza minima e divieto di
 * riusare la password corrente ci sono da tempo, la blocklist no.
 *
 * ## Perché un elenco di stringhe da confrontare tal quali non serve a niente
 *
 * La tentazione, leggendo «blocklist delle password più comuni», è di prendere
 * uno dei mille elenchi che circolano — `123456`, `password`, `qwerty`,
 * `letmein` — e rifiutare le stringhe che ci compaiono dentro. Sarebbe
 * teatro. Prima che quel confronto avvenga, una password ha già dovuto
 * attraversare due regole: dodici caratteri di minimo
 * (`security.minPasswordLength`) e le tre classi di `PASSWORD_CLASSI`, cioè una
 * maiuscola, una minuscola e una cifra. `password` è lunga otto, tutta
 * minuscola e senza cifre: è già rifiutata tre volte. Lo stesso vale per la
 * quasi totalità di quegli elenchi, che sono elenchi di password *vere*, raccolte
 * dove nessuno imponeva né lunghezza né classi.
 *
 * Un elenco crudo, qui, non rifiuterebbe **una sola** password che oggi passa. E
 * sarebbe peggio che inutile: una riga verde nel registro, una casella spuntata
 * nell'audit, e nessuna protezione in più.
 *
 * ## Che cosa passa davvero, e che cosa serve rifiutare
 *
 * Le regole in vigore non impediscono di scegliere una password prevedibile:
 * impongono di **decorarla**. Chi scriveva `password` scrive `Password2026`.
 * Chi scriveva `ciao` scrive `Ciao1234567!`. L'ufficio del personale che deve
 * comporre una provvisoria scrive `Benvenuto1`. La forma tipica non è la parola
 * nuda, è la parola con una maiuscola davanti, un anno dietro e ogni tanto un
 * punto esclamativo in fondo — e quella forma soddisfa tutte le regole che
 * abbiamo, una per una.
 *
 * Quindi la blocklist non confronta la password: confronta la **radice** che
 * resta quando si tolgono le decorazioni. `Password2026` e `P4ssw0rd!` e
 * `password` diventano tutte e tre `password`, e `password` sta nell'elenco.
 *
 * ## Le due radici, e perché sono due
 *
 * Una sola riduzione non basta, e le due che ci sono coprono due abitudini
 * diverse:
 *
 * - la **nuda** butta via tutto ciò che non è una lettera, ovunque si trovi.
 *   Prende `Beste2026HR` e ne fa `bestehr`, cioè riconosce chi *infila* le cifre
 *   in mezzo alla parola invece di metterle in coda.
 * - la **sfrondata** toglie prima le cifre e i simboli **agli estremi**, poi
 *   traduce i caratteri usati al posto delle lettere (`0` per `o`, `4` per `a`,
 *   `$` per `s`…), poi butta via il resto. Prende `P4ssw0rd!` e ne fa
 *   `password`.
 *
 * Perché non una sola riduzione che faccia tutto: se si traducessero le cifre
 * *ovunque*, `Password2026` diventerebbe `passwordzozo`, e la forma più comune
 * di tutte — parola più anno — smetterebbe di essere riconosciuta. La
 * traduzione delle cifre ha senso solo **dentro** la parola, dove una cifra sta
 * al posto di una lettera; in coda una cifra è un anno o un contatore, e va
 * tolta, non tradotta. Le due riduzioni dicono esattamente questo, ciascuna per
 * la sua metà.
 *
 * ## Le due regole che un elenco non può contenere
 *
 * `Aaaaaaaaaaa1` e `Qwertyuiop12` non sono parole: sono un dito tenuto premuto e
 * una riga di tastiera. Non si mettono in un elenco perché sono infinite — si
 * riconoscono guardando la forma. Sono le due regole di
 * `ripetizione()` e `sequenzaDiTastiera()`.
 *
 * ## Dove sta, e perché non sul server
 *
 * Sta in `@bestehr/shared` per la stessa ragione per cui ci sta
 * `PASSWORD_CLASSI`, ed è scritta là per esteso: le due spunte che il pannello e
 * la app disegnano accanto alla casella devono essere **la stessa regola** del
 * server, non una che le somiglia. Una blocklist applicata solo dal server
 * produrrebbe di nuovo il guasto che quel commento racconta per due volte —
 * elenco tutto verde, pulsante acceso, e un 400 dopo la pressione — con
 * l'aggravante che stavolta il rifiuto arriverebbe su una password che a chi
 * l'ha scritta sembra a posto.
 *
 * ## Che cosa questa regola **non** fa
 *
 * Non guarda chi sta scegliendo. `Giuseppe1985` è rifiutata perché `giuseppe` è
 * nell'elenco dei nomi comuni, non perché la persona si chiama Giuseppe: il
 * confronto con il proprio nome, il proprio indirizzo e la propria matricola è
 * un'altra regola, che ha bisogno di dati che questa funzione non riceve, e non
 * è stata scritta perché A16 non la chiede. È annotata come cosa da proporre,
 * non fatta di nascosto.
 */
/**
 * Le lettere accentate, ridotte alla loro base.
 *
 * Serve perché `Perché2026` e `Perche2026` sono la stessa password per chiunque
 * la debba indovinare, e devono esserlo anche per l'elenco.
 *
 * È una tabella e non `normalize('NFD')` di proposito. La normalizzazione
 * Unicode farebbe lo stesso lavoro in una riga, ma questa riga gira anche su
 * Hermes, il motore della app, dove le tabelle Unicode complete non sono una
 * garanzia — e una funzione di sicurezza che su un motore riduce e sull'altro no
 * sarebbe la solita regola che vale in un punto e non nell'altro. Le lettere che
 * servono a un'azienda italiana sono queste dodici, si scrivono una volta, e
 * funzionano ovunque.
 */
const SENZA_ACCENTO = {
    à: 'a',
    á: 'a',
    è: 'e',
    é: 'e',
    ì: 'i',
    í: 'i',
    î: 'i',
    ò: 'o',
    ó: 'o',
    ù: 'u',
    ú: 'u',
    ç: 'c',
};
/**
 * I caratteri che si usano **al posto** di una lettera.
 *
 * Solo quelli su cui non c'è ambiguità di lettura. `2` per `z` e `9` per `g`
 * esistono, ma sono rari quanto sono frequenti `2` come «due» e `9` come cifra
 * di un anno: tradurli produrrebbe più radici sbagliate che riconoscimenti.
 */
const AL_POSTO_DI = {
    '0': 'o',
    '1': 'i',
    '!': 'i',
    '|': 'i',
    '3': 'e',
    '4': 'a',
    '@': 'a',
    '5': 's',
    $: 's',
    '7': 't',
};
function minuscolaSenzaAccenti(password) {
    let fuori = '';
    for (const c of password.toLowerCase())
        fuori += SENZA_ACCENTO[c] ?? c;
    return fuori;
}
/**
 * Le radici da confrontare con l'elenco: la nuda e la sfrondata.
 *
 * Restituisce sempre e solo radici non vuote, e senza doppioni — sulla
 * stragrande maggioranza delle password le due riduzioni danno lo stesso
 * risultato, e confrontarlo due volte non serve a nessuno.
 *
 * È esportata perché la prova (`scripts/prova-password.mjs`) mostra la radice
 * accanto alla password quando un caso fallisce: senza, un rosso direbbe «questa
 * doveva essere rifiutata» senza dire in che cosa il calcolo si sia perso.
 */
export function radiciDiPassword(password) {
    const bassa = minuscolaSenzaAccenti(password);
    const nuda = bassa.replace(/[^a-z]/g, '');
    const sfrondata = bassa
        .replace(/^[^a-z]+/, '')
        .replace(/[^a-z]+$/, '')
        .split('')
        .map((c) => AL_POSTO_DI[c] ?? c)
        .join('')
        .replace(/[^a-z]/g, '');
    return [...new Set([nuda, sfrondata])].filter((r) => r.length > 0);
}
/**
 * Le file di tasti, l'alfabeto e le cifre: le tracce su cui un dito scivola.
 *
 * Le cifre finiscono con lo zero di nuovo dopo il nove perché sulla tastiera lo
 * zero sta **dopo** il nove, e `7890` è la coda che la gente scrive davvero.
 */
const TRACCE = [
    'qwertyuiop',
    'asdfghjkl',
    'zxcvbnm',
    'abcdefghijklmnopqrstuvwxyz',
    '01234567890',
];
/**
 * Sei, e non quattro né otto.
 *
 * Sotto i sei si comincia a rifiutare per caso: `abcd` e `1234` compaiono dentro
 * parole e date che nessuno ha scelto pensando alla tastiera. Sopra i sei si
 * lascia passare `Qwerty2026`, che è esattamente il caso per cui la regola
 * esiste.
 */
const CORSA_MINIMA = 6;
function sequenzaDiTastiera(bassa) {
    for (let i = 0; i + CORSA_MINIMA <= bassa.length; i++) {
        const finestra = bassa.slice(i, i + CORSA_MINIMA);
        const rovescio = finestra.split('').reverse().join('');
        if (TRACCE.some((t) => t.includes(finestra) || t.includes(rovescio)))
            return true;
    }
    return false;
}
/**
 * Il blocco più lungo che può essere ripetuto a riempire la password.
 *
 * Quattro perché `Ab12Ab12Ab12` è un blocco da quattro ed è la ripetizione più
 * lunga che una persona compone a mano; oltre, si smette di ripetere e si
 * comincia a scrivere.
 */
const BLOCCO_MASSIMO = 4;
function ripetizione(bassa, radici) {
    // Un dito tenuto premuto: la radice è tutta la stessa lettera. Guarda la
    // radice e non la password perché `Aaaaaaaaaaa1` ha tre caratteri distinti e
    // una lettera sola, ed è il caso che si vuole prendere.
    if (radici.some((r) => new Set(r).size === 1))
        return true;
    // Un blocco corto ricopiato fino a riempire: `Ab1Ab1Ab1Ab1`.
    for (let n = 1; n <= BLOCCO_MASSIMO; n++) {
        if (bassa.length <= n || bassa.length % n !== 0)
            continue;
        const blocco = bassa.slice(0, n);
        if (blocco.repeat(bassa.length / n) === bassa)
            return true;
    }
    return false;
}
/**
 * Le radici che non si possono usare, raccolte per famiglia.
 *
 * Sono radici, non password: si confrontano con quel che resta dopo le
 * riduzioni, quindi vanno scritte **nude** — minuscole, senza cifre, senza
 * accenti. Una riga scritta come `Password2026` non prenderebbe mai niente.
 *
 * L'elenco è corto di proposito. Le liste da diecimila voci sono liste di
 * password intere, e nove decimi delle loro righe qui sarebbero morte: o non
 * sono radici, o sono radici che nessuna password conforme può avere. Quello che
 * conta è coprire le famiglie da cui la gente **pesca** quando le si chiede una
 * parola: le classiche di ogni elenco, l'italiano di tutti i giorni, i nomi di
 * battesimo, il calendario, le città, il lessico dell'ufficio quando compone una
 * provvisoria, e il nome dell'azienda.
 */
const CLASSICHE = [
    'password',
    'passwd',
    'pass',
    'qwerty',
    'qwertyuiop',
    'asdfgh',
    'asdfghjkl',
    'zxcvbn',
    'zxcvbnm',
    'letmein',
    'welcome',
    'monkey',
    'dragon',
    'shadow',
    'master',
    'sunshine',
    'princess',
    'football',
    'baseball',
    'iloveyou',
    'trustno',
    'whatever',
    'freedom',
    'starwars',
    'superman',
    'batman',
    'pokemon',
    'computer',
    'internet',
    'secret',
    'hello',
    'login',
    'admin',
    'administrator',
    'root',
    'guest',
    'user',
    'default',
    'changeme',
    'abcdef',
    'abcdefgh',
];
const ITALIANE = [
    'ciao',
    'ciaociao',
    'amore',
    'amoremio',
    'tiamo',
    'tiamotanto',
    'casa',
    'casamia',
    'sole',
    'luna',
    'stella',
    'mamma',
    'papa',
    'famiglia',
    'segreto',
    'parola',
    'paroladordine',
    'pizza',
    'calcio',
    'italia',
    'juventus',
    'milan',
    'inter',
    'ferrari',
    'vespa',
    'cane',
    'gatto',
    'libero',
    'liberta',
    'felice',
    'buongiorno',
    'buonasera',
    'grazie',
    'prego',
    'scusa',
    'perche',
    'andiamo',
    'vaffanculo',
    'cazzo',
    'merda',
    'stronzo',
];
/**
 * I nomi di battesimo.
 *
 * `Giuseppe1985` è la password italiana per eccellenza: un nome, una data, e le
 * tre classi soddisfatte senza pensarci. Il nome è rifiutato **come parola
 * comune**, non perché sia il nome di chi lo scrive — quel confronto è un'altra
 * regola e non c'è (vedi il commento in cima).
 *
 * Sessanta e non seicento: la coda lunga dei nomi rari non è la coda lunga delle
 * password, perché chi si chiama in un modo raro non è più probabile che ci
 * costruisca sopra una password, e ogni riga in più è una parola tolta a chi la
 * userebbe dentro una passphrase onesta.
 */
const NOMI = [
    'giuseppe',
    'giovanni',
    'antonio',
    'mario',
    'luigi',
    'francesco',
    'angelo',
    'vincenzo',
    'pietro',
    'salvatore',
    'carlo',
    'franco',
    'domenico',
    'bruno',
    'paolo',
    'michele',
    'giorgio',
    'sergio',
    'luca',
    'marco',
    'andrea',
    'alessandro',
    'matteo',
    'lorenzo',
    'davide',
    'simone',
    'stefano',
    'roberto',
    'alberto',
    'fabio',
    'daniele',
    'riccardo',
    'federico',
    'gabriele',
    'emanuele',
    'nicola',
    'maria',
    'anna',
    'giuseppina',
    'rosa',
    'angela',
    'giovanna',
    'teresa',
    'lucia',
    'carmela',
    'caterina',
    'francesca',
    'laura',
    'paola',
    'elena',
    'chiara',
    'sara',
    'giulia',
    'martina',
    'alessia',
    'valentina',
    'silvia',
    'cristina',
    'barbara',
    'daniela',
    'federica',
    'alessandra',
    'roberta',
    'claudia',
    'elisa',
];
const CALENDARIO = [
    'gennaio',
    'febbraio',
    'marzo',
    'aprile',
    'maggio',
    'giugno',
    'luglio',
    'agosto',
    'settembre',
    'ottobre',
    'novembre',
    'dicembre',
    'primavera',
    'estate',
    'autunno',
    'inverno',
    'natale',
    'pasqua',
    'ferragosto',
    'capodanno',
    'ferie',
    'vacanza',
    'vacanze',
];
const LUOGHI = [
    'roma',
    'milano',
    'napoli',
    'torino',
    'palermo',
    'genova',
    'bologna',
    'firenze',
    'bari',
    'catania',
    'venezia',
    'verona',
    'padova',
    'brescia',
    'prato',
    'toscana',
    'sicilia',
    'sardegna',
];
/**
 * Il lessico di chi compone una password **per un altro**.
 *
 * È la famiglia che conta di più, ed è quella che nessun elenco pubblico
 * contiene: una password provvisoria non finisce in una fuga di dati, quindi non
 * finisce nelle statistiche. Chi crea una scheda scrive `Benvenuto1`,
 * `Provvisoria2026`, `Cambiami1`, e lo scrive **sempre uguale** per tutte le
 * schede che apre in un pomeriggio — che è il difetto vero: non una password
 * debole, una password debole e **condivisa**.
 */
const UFFICIO = [
    'benvenuto',
    'benvenuta',
    'benvenuti',
    'cambiami',
    'cambia',
    'temporanea',
    'temporaneo',
    'provvisoria',
    'provvisorio',
    'iniziale',
    'primoaccesso',
    'accesso',
    'ingresso',
    'utente',
    'dipendente',
    'personale',
    'ufficio',
    'azienda',
    'aziendale',
    'lavoro',
    'nuovo',
    'nuova',
    'prova',
    'test',
    'esempio',
    'demo',
];
/**
 * Le parole di casa.
 *
 * Il nome dell'azienda è la prima cosa che viene in mente a chiunque debba
 * inventare una password *su un sistema aziendale*, e non sta in nessun elenco
 * pubblico perché è una parola sola al mondo. Ci sono anche i nomi dei sistemi,
 * perché `Besteferie2026` è verosimile quanto `Bestehr2026`.
 */
const AZIENDA = ['beste', 'bestehr', 'bestespa', 'besteferie', 'bestesrl', 'hr', 'portale'];
/** L'elenco intero, appiattito e senza doppioni. */
export const PASSWORD_BANALI = new Set([
    ...CLASSICHE,
    ...ITALIANE,
    ...NOMI,
    ...CALENDARIO,
    ...LUOGHI,
    ...UFFICIO,
    ...AZIENDA,
]);
/**
 * Vero quando la password è prevedibile, e quindi non si può scegliere.
 *
 * Le tre condizioni sono in `or` e sono tenute separate perché misurano cose
 * diverse: una parola nota, un dito che scivola, un dito che resta fermo.
 */
export function passwordBanale(password) {
    const bassa = minuscolaSenzaAccenti(password);
    const radici = radiciDiPassword(password);
    if (radici.some((r) => PASSWORD_BANALI.has(r)))
        return true;
    if (sequenzaDiTastiera(bassa))
        return true;
    if (ripetizione(bassa, radici))
        return true;
    return false;
}
/**
 * Il divieto nella forma in cui i due client lo disegnano.
 *
 * Stessa forma di `PASSWORD_CLASSI` — una prova, la frase per il pannello, la
 * chiave per la app — perché i due elenchi di requisiti si compongono nello
 * stesso posto e nessuno dei due deve poter dimenticare l'altro.
 *
 * `prova` è vera quando il requisito è **soddisfatto**, cioè quando la password
 * *non* è banale: le spunte a schermo sono verdi quando va bene, e un requisito
 * che si accendesse al contrario sarebbe l'unico dell'elenco a farlo.
 *
 * La frase dice le tre cose da evitare invece di dire quale delle tre è scattata.
 * Non è pigrizia: chi legge deve **cambiare** la password, e sapere che ha scritto
 * una sequenza di tastiera invece di una parola comune non lo aiuta a scriverne
 * una buona, mentre l'elenco delle tre gli dice da che parte non andare. Il
 * rifiuto puntuale costerebbe per giunta tre frasi in tre lingue, tenute
 * allineate a mano, per un'informazione che nessuno usa.
 *
 * ## La casella vuota
 *
 * `password !== ''` non è una difesa contro un argomento sbagliato: è la
 * differenza fra «soddisfatto» e «non ancora». `passwordBanale('')` risponde
 * `false` — una stringa vuota non contiene nessuna radice, nessuna sequenza e
 * nessuna ripetizione, e risponde bene — ma girata in `prova` diventava una
 * spunta **verde su un campo mai toccato**, l'unica accesa delle cinque.
 *
 * Lo ha fatto vedere la scena 119 dell'anteprima, la prima a fotografare il
 * campo della password nuova ancora vuoto: quattro righe spente e la quinta
 * accesa. A chi guarda, quella riga dice «questa l'hai già passata» prima che
 * abbia scritto una lettera, e quando scriverà si spegnerà — cioè si muoverà
 * all'indietro, che è l'unico modo in cui un elenco di requisiti non deve mai
 * muoversi.
 *
 * Solo qui e non dentro `passwordBanale`: là la risposta è giusta, e il server
 * la usa per **rifiutare**. Una stringa vuota il server la ferma con la
 * lunghezza minima, e fargliela chiamare «banale» sarebbe una frase falsa in
 * faccia a chi non ha scritto niente.
 */
export const PASSWORD_DIVIETO = {
    prova: (password) => password !== '' && !passwordBanale(password),
    richiesta: 'Non una parola comune, un nome, una sequenza di tastiera o una ripetizione',
    chiave: 'password.banale',
};
//# sourceMappingURL=password-comuni.js.map